import { useState, useEffect } from 'react'
import { ReactNode } from 'react'
import { useAuth } from '../hooks/useAuth'
import {
  LayoutDashboard,
  FolderKanban,
  PlusCircle,
  Settings,
  LogOut,
  Bell,
  Check,
  X,
} from 'lucide-react'
import { Link, useLocation, Navigate } from 'react-router-dom'
import { getUnreadNotifications, markNotificationAsRead, markAllNotificationsAsRead, getUpcomingNodes } from '../lib/api'
import type { Notification } from '../types'

export default function Layout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth()
  const location = useLocation()
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([])
  const [showNotifications, setShowNotifications] = useState(false)
  const [loading, setLoading] = useState(false)

  const loadNotifications = async () => {
    try {
      setLoading(true)
      const [notifications, upcomingNodes] = await Promise.all([
        getUnreadNotifications().catch(() => []),
        getUpcomingNodes(7).catch(() => []),
      ])
      
      // Merge notifications and upcoming nodes
      const allNotifications = [
        ...(notifications || []),
        ...(upcomingNodes || []).map((n: Notification) => ({
          ...n,
          read: false,
        })),
      ]
      
      setUnreadNotifications(allNotifications)
    } catch (err) {
      console.error('加载通知失败:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadNotifications()
  }, [])

  const markAsRead = async (id: string) => {
    await markNotificationAsRead(id)
    setUnreadNotifications(prev => prev.filter(n => n.id !== id))
  }

  const markAllAsRead = async () => {
    await markAllNotificationsAsRead()
    setUnreadNotifications([])
    setShowNotifications(false)
  }

  const navItems = [
    { path: '/dashboard', label: '仪表盘', icon: LayoutDashboard },
    { path: '/cases', label: '案件列表', icon: FolderKanban },
    { path: '/cases/new', label: '新建案件', icon: PlusCircle },
    { path: '/settings', label: '系统设置', icon: Settings },
  ]

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col">
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center font-bold text-sm">诉</div>
            <div>
              <div className="font-semibold text-sm">诉讼记录</div>
              <div className="text-xs text-slate-400">Litigation Pro</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-2">
          {navItems.map(item => {
            const Icon = item.icon
            const isActive = location.pathname === item.path
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-indigo-600/20 text-indigo-400 border-l-2 border-indigo-400'
                    : 'hover:bg-white/5 text-slate-300'
                }`}
              >
                <Icon size={18} />
                {item.label}
              </Link>
            )
          })}
        </nav>

        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-xs">
              {user?.display_name?.[0] || user?.username?.[0] || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.display_name || user?.username}</div>
              <div className="text-xs text-slate-400">
                {user?.role === 'admin' ? '系统管理员' : '子账号'}
              </div>
            </div>
          </div>
          <button
            onClick={() => signOut()}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <LogOut size={16} />
            退出登录
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="h-14 bg-white border-b border-gray-200 flex items-center px-6 gap-4">
          <h1 className="text-lg font-semibold">{getBreadcrumb(location.pathname)}</h1>
          <div className="flex-1" />
          
          {/* Notifications */}
          <div className="relative">
            <button 
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <Bell size={20} />
              {unreadNotifications.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center">
                  {unreadNotifications.length > 99 ? '99+' : unreadNotifications.length}
                </span>
              )}
            </button>

            {/* Notification Dropdown */}
            {showNotifications && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowNotifications(false)} />
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-20 overflow-hidden">
                  <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-sm font-medium">通知</span>
                    {unreadNotifications.length > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-xs text-indigo-600 hover:text-indigo-700"
                      >
                        全部已读
                      </button>
                    )}
                  </div>
                  
                  <div className="max-h-96 overflow-auto">
                    {loading ? (
                      <div className="p-4 text-center text-gray-400 text-sm">加载中...</div>
                    ) : unreadNotifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-400 text-sm">暂无通知</div>
                    ) : (
                      unreadNotifications.map(notification => (
                        <div
                          key={notification.id}
                          className="p-3 border-b border-gray-50 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-800 truncate">
                                {notification.title}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {notification.body}
                              </div>
                            </div>
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="p-1 hover:bg-gray-200 rounded transition-colors"
                              title="标记已读"
                            >
                              <Check size={14} className="text-gray-400" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {unreadNotifications.length > 0 && (
                    <div className="p-2 border-t border-gray-100">
                      <button
                        onClick={() => setShowNotifications(false)}
                        className="w-full text-center text-xs text-gray-500 hover:text-gray-700"
                      >
                        关闭
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </header>
        <div className="flex-1 overflow-auto p-6">
          {children}
        </div>
      </main>
    </div>
  )
}

function getBreadcrumb(pathname: string): string {
  const map: Record<string, string> = {
    '/dashboard': '仪表盘',
    '/cases': '案件列表',
    '/cases/new': '新建案件',
    '/settings': '系统设置',
  }
  if (pathname.startsWith('/cases/') && pathname !== '/cases/new') {
    return '案件详情'
  }
  return map[pathname] || '诉讼记录系统'
}
